import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { GoogleMapsAutocompleteDirective } from '@app/directives/google-maps-autocomplete/google-maps-autocomplete.directive';
import { AlertComponent } from '@components/atoms/alert/alert.component';

import {
    MdsCheckboxComponent,
    MdsChipComponent,
    MdsDatepickerComponent,
    MdsDropdownComponent,
    MdsRadioButton,
    MdsRadioGroupComponent,
    MdsTextareaComponent,
    MdsTextFieldComponent,
} from '@medpacesoftwaredevelopment/designsystem';

import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MileageReimbursementRateService } from '@app/v2/services/mileage-reimbursement-rate/mileage-reimbursement-rate.service';
import { Expense } from '@models/request';
import { CountryStateService } from '@services/state-management/country-state.service';
import { PersistentFormControl } from '@utility/persistent-forms';
import { isTypeMileageExpense, parseDistanceUnit } from '@utility/utility';
import { catchError, combineLatest, EMPTY, filter, map, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { BaseExpenseComponent } from '../base-expense-edit.component';
import { MileageExpenseFormGroup } from './mileage-expense-edit.form';

@Component({
    selector: 'medpace-mileage-expense-edit',
    imports: [
        MdsTextFieldComponent,
        MdsCheckboxComponent,
        MdsRadioGroupComponent,
        MdsDropdownComponent,
        MdsChipComponent,
        AlertComponent,
        GoogleMapsAutocompleteDirective,
        MdsDatepickerComponent,
        AsyncPipe,
        MdsTextareaComponent,
    ],
    templateUrl: './mileage-expense-edit.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class MileageExpenseEditComponent extends BaseExpenseComponent<MileageExpenseFormGroup> {
    public readonly distanceUnit_RadioButtons: MdsRadioButton[] = [
        {
            label: 'Miles',
            value: 'Miles',
            name: 'distanceUnit_Miles',
            id: 'distanceUnit_Miles',
        },
        {
            label: 'Kilometers',
            value: 'Kilometers',
            name: 'distanceUnit_Kilometers',
            id: 'distanceUnit_Kilometers',
        },
    ];
    countryStateService = inject(CountryStateService);
    mileageReimbursementRateService = inject(MileageReimbursementRateService);
    countryOptions = toSignal(this.countryStateService.getActiveCountriesAsMdsOptions());
    googleMapsAutocompletePlaceChange(place: google.maps.places.Place, formControl: PersistentFormControl<string>) {
        formControl.setValue(place.formattedAddress);

        const country = place.addressComponents?.find((component) => component.types.includes('country'));
        if (country) {
            const countryTwoCharCode = country.shortText;
            const selectedCountry =
                this.countryOptions()?.find((country) => country.value.twoCharCode === countryTwoCharCode)?.value ??
                null;
            if (selectedCountry) this.formGrp().controls.country.setValue(selectedCountry);
        }
    }
    override push(expense: Expense): Expense {
        super.push(expense);

        const value = this.formGrp().getRawValue();
        expense.manualTotalCalculation = value.manualTotalCalculation;
        expense.mileageComments = value.comment;
        expense.mileageDistanceUnits =
            value.distanceUnit === 'Kilometers' ? 'k' : value.distanceUnit === 'Miles' ? 'm' : undefined;
        expense.mileageStartLocation = value.fromLocation;
        expense.mileageEndLocation = value.toLocation;
        expense.mileageReimbursementCountry = value.country.threeCharCode;
        expense.mileageReimbursementRatePerUnit = !!value.reimbursementRate ? +value.reimbursementRate : undefined;
        expense.mileageTotalDistance = +value.totalDistance;
        expense.isIncomplete = value.isIncomplete;

        return expense;
    }
    reimbursementRate$ = this.formGrp$.pipe(
        switchMap((formGrp) =>
            formGrp.controls.reimbursementRate.valueChangesReplay.pipe(
                map((valueString) => {
                    if (!valueString) return null;
                    const parsed = Number(valueString);
                    return isNaN(parsed) ? null : parsed;
                }),
                filter((rate) => rate !== null)
            )
        )
    );
    totalDistance$ = this.formGrp$.pipe(
        switchMap((formGrp) =>
            formGrp.controls.totalDistance.valueChangesReplay.pipe(
                map((valueString) => {
                    if (!valueString) return null;
                    const parsed = Number(valueString);
                    return isNaN(parsed) ? null : parsed;
                })
            )
        )
    );
    manualTotalCalculation$ = this.formGrp$.pipe(
        switchMap((formGrp) => formGrp.controls.manualTotalCalculation.valueChangesReplay)
    );
    receiptDate$ = this.expenseFormGroup$.pipe(switchMap((formGrp) => formGrp.controls.receiptDate.valueChangesReplay));
    country$ = this.formGrp$.pipe(switchMap((formGrp) => formGrp.controls.country.valueChangesReplay));
    isIncomplete$ = this.formGrp$.pipe(switchMap((formGrp) => formGrp.controls.isIncomplete.valueChangesReplay));
    reimbursementRateFromServer$ = combineLatest({
        manualTotalCalculation: this.manualTotalCalculation$,
        receiptDate: this.receiptDate$,
        country: this.country$,
    }).pipe(
        filter(({ country, receiptDate, manualTotalCalculation }) => {
            return !manualTotalCalculation && !!country && !!receiptDate && this.expenseFormGroup().dirty; // only allow user actions
        }),
        switchMap(({ country, receiptDate }) => {
            return this.mileageReimbursementRateService.get(country.threeCharCode, receiptDate).pipe(
                catchError((err) => {
                    if (err instanceof HttpErrorResponse) {
                        const error = err;
                        if (error.status === 404) {
                            // no such rate in the db,
                            this.formGrp().controls.isIncomplete.setValue(true);
                            return EMPTY;
                        }
                    }
                    return throwError(() => err);
                }),
                tap(() => {
                    this.formGrp().controls.isIncomplete.setValue(false);
                })
            );
        }),
        shareReplay(1)
    );
    updateReimbursementRateEffect$ = combineLatest({
        reimbursementRate: this.reimbursementRateFromServer$,
        currencies: this.currencyStateService.getCurrencies(),
    })
        .pipe(
            tap(({ currencies, reimbursementRate }) => {
                const currency =
                    currencies.find((currency) => currency.isocode === reimbursementRate.currencyCode) ?? null;
                const distanceUnit = parseDistanceUnit(reimbursementRate.mileageDistanceUnits);
                this.expenseFormGroup().patchValue(
                    {
                        currency,
                        mileageExpense: {
                            distanceUnit,
                            reimbursementRate: reimbursementRate.mileageReimbursementRatePerUnit.toFixed(3),
                        },
                    },
                    { emitEvent: true }
                );
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    patchValue$ = combineLatest({
        expense: this.expenseNotNull$,
        countries: this.countryStateService.getActiveCountriesAsMdsOptions(),
    })
        .pipe(
            filter(({ expense }) => isTypeMileageExpense(expense.expenseId)), // only update form when existing request is mileage, ignore if user manually changed the form
            tap(({ countries, expense }) => {
                const expenseCountryLowerCase = expense.mileageReimbursementCountry.toLowerCase();
                const country =
                    countries.find(
                        (country) =>
                            country.value.threeCharCode.toLowerCase() === expenseCountryLowerCase ||
                            country.value.name.toLowerCase() === expenseCountryLowerCase
                    )?.value ?? null;

                this.patchFormGroup(expense);
                this.formGrp().patchValue(
                    {
                        fromLocation: expense.mileageStartLocation,
                        toLocation: expense.mileageEndLocation,
                        country,
                        manualTotalCalculation: expense.manualTotalCalculation,
                        reimbursementRate: expense.mileageReimbursementRatePerUnit?.toFixed(3), // might be undefined when manualTotalCalculation=true
                        totalDistance: expense.mileageTotalDistance?.toString() ?? null,
                        comment: expense.mileageComments,
                        distanceUnit: parseDistanceUnit(expense.mileageDistanceUnits),
                    },
                    { emitEvent: true }
                );
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    setTotalCalculationModeEffect$ = this.manualTotalCalculation$
        .pipe(
            tap((manualTotalCalculation) => {
                if (manualTotalCalculation) {
                    this.formGrp().controls.requestedAmountInLocalCurrency.enable({ force: true, emitEvent: true });
                    this.formGrp().controls.reimbursementRate.disable({ persistent: true, emitEvent: true });
                } else {
                    // false - set by user, or null - default when form is clear
                    this.formGrp().controls.requestedAmountInLocalCurrency.disable({
                        persistent: true,
                        emitEvent: true,
                    });
                    this.formGrp().controls.reimbursementRate.enable({ force: true, emitEvent: true });
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();
    totalRequested$ = combineLatest({
        reimbursementRate: this.reimbursementRate$,
        totalDistance: this.totalDistance$,
    }).pipe(map(({ reimbursementRate, totalDistance }) => reimbursementRate * totalDistance));

    setTotalRequestedEffect$ = combineLatest({
        manualTotalCalculation: this.manualTotalCalculation$,
        totalRequested: this.totalRequested$,
    })
        .pipe(
            filter(({ manualTotalCalculation }) => !manualTotalCalculation), // can be undefined/null or false
            tap(({ totalRequested }) => {
                this.formGrp().controls.requestedAmountInLocalCurrency.setValue(totalRequested.toFixed(2));
            }),
            takeUntilDestroyed()
        )
        .subscribe();
}
